/* Cold Case Press — storefront behaviour. Native Web Components: each instance sets itself up in
   connectedCallback and tears down in disconnectedCallback, so Theme Editor section reloads never
   leave duplicate listeners behind. All commerce goes through Shopify's Cart AJAX API. */

const doc = document;
const root = doc.documentElement;
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = () => root.classList.contains('shopify-design-mode') || (window.Shopify && window.Shopify.designMode);

/* ---------------- Config & helpers ---------------- */
let _cfg;
function cfg() {
  if (_cfg) return _cfg;
  const el = doc.getElementById('ccp-config');
  try { _cfg = el ? JSON.parse(el.textContent) : {}; } catch (e) { _cfg = {}; }
  _cfg.strings = _cfg.strings || {};
  _cfg.cases = _cfg.cases || [];
  _cfg.tiers = (_cfg.tiers || []).filter((t) => t.count > 1 && t.discount > 0).sort((a, b) => a.count - b.count);
  return _cfg;
}
function t(key, vars) {
  let s = cfg().strings[key] || key;
  if (vars) Object.keys(vars).forEach((k) => { s = s.split('[' + k + ']').join(vars[k]); });
  return s;
}
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
export function numWord(n) { const w = (cfg().strings || {}).numbers; return (Array.isArray(w) && w[n]) || String(n); }
export function money(cents) {
  const format = cfg().moneyFormat || '€{{amount_with_comma_separator}}';
  const value = Number(cents) || 0;
  const fmt = (n, dec, th, dp) => {
    const fixed = (n / 100).toFixed(dec);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
    return parts.length > 1 ? parts[0] + dp + parts[1] : parts[0];
  };
  const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
    switch (key) {
      case 'amount': return fmt(value, 2, ',', '.');
      case 'amount_no_decimals': return fmt(value, 0, ',', '.');
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
      default: return fmt(value, 2, ',', '.');
    }
  });
  return value % 100 === 0 ? out.replace(/([.,])00(?!\d)/, '') : out;
}
function caseByVariant(id) { return cfg().cases.find((c) => String(c.variantId) === String(id)); }
function caseByProduct(id) { return cfg().cases.find((c) => String(c.productId) === String(id)); }
function isVolumeVariant(id) { const v = cfg().volume; return !!(v && String(v.variantId) === String(id)); }
function bundleByVariant(id) { return (cfg().bundles || []).find((b) => String(b.variantId) === String(id)); }
function isVolumeProduct(id) { const v = cfg().volume; return !!(v && String(v.productId) === String(id)); }
/** Best configured volume tier for a number of eligible cases (discount in presentment cents). */
export function bestTier(count) {
  let best = null;
  cfg().tiers.forEach((tier) => { if (count >= tier.count && (!best || tier.discount > best.discount)) best = tier; });
  return best;
}
export function nextTier(count) { return cfg().tiers.find((tier) => tier.count > count) || null; }

/* ---------------- Scroll container ----------------
   Horizon 4.x scrolls inside .page-wrapper (html/body are fixed at 100dvh), older layouts scroll the window. */
export function scroller() {
  const pw = doc.querySelector('.page-wrapper');
  if (pw && pw.scrollHeight > pw.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(pw).overflowY)) return pw;
  return null;
}
export function scrollTop() { const s = scroller(); return s ? s.scrollTop : (window.scrollY || 0); }

/* ---------------- Scroll lock & focus trap ---------------- */
let lockCount = 0;
let lockTarget = null;
function lockScroll() {
  if (lockCount++ === 0) {
    const sc = scroller();
    const target = sc || doc.body;
    const sb = sc ? sc.offsetWidth - sc.clientWidth : window.innerWidth - root.clientWidth;
    root.style.setProperty('--ccp-sb', sb + 'px');
    target.style.paddingRight = sb > 0 ? sb + 'px' : '';
    lockTarget = target;
    root.classList.add('ccp-lock');
  }
}
function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) { root.classList.remove('ccp-lock'); if (lockTarget) lockTarget.style.paddingRight = ''; lockTarget = null; }
}
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === doc.activeElement);
  if (!items.length) return;
  const first = items[0], last = items[items.length - 1];
  if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
}

/* ---------------- Cart API ---------------- */
const Cart = {
  state: null,
  pending: false,
  root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || cfg().root || '/'; },
  async request(path, body) {
    const res = await fetch(this.root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    });
    let data = null;
    try { data = await res.json(); } catch (e) { /* no body */ }
    if (!res.ok) {
      const msg = (data && (data.description || data.message)) || t('error_generic');
      const err = new Error(typeof msg === 'string' ? msg : t('error_generic'));
      err.status = res.status;
      throw err;
    }
    return data;
  },
  async get() { this.state = await this.request('cart.js'); this.emit(); return this.state; },
  emit() { doc.dispatchEvent(new CustomEvent('ccp:cart', { detail: { cart: this.state } })); },
  lineFor(variantId) { return this.state ? this.state.items.find((i) => String(i.variant_id) === String(variantId)) : null; },
  /** Adds variants, enforcing one-copy rules for case files and the complete volume. Returns {added, skipped}. */
  async add(items, options = {}) {
    if (this.pending) return { added: [], skipped: [], busy: true };
    this.pending = true;
    try {
      if (!this.state) await this.get();
      const c = cfg();
      const volumeInCart = c.volume && this.lineFor(c.volume.variantId);
      const skipped = [];
      const toAdd = [];
      items.forEach((item) => {
        const id = String(item.id);
        const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
        const isCase = !!caseByVariant(id);
        const isVol = isVolumeVariant(id) || !!bundleByVariant(id);
        if (c.limitQty && (isCase || isVol)) {
          if (this.lineFor(id)) { skipped.push({ id, reason: 'in_cart' }); return; }
          if (isCase && volumeInCart) { skipped.push({ id, reason: 'in_volume' }); return; }
          if (toAdd.some((x) => x.id === id)) return;
          toAdd.push({ id, quantity: 1 });
        } else {
          toAdd.push({ id, quantity: qty });
        }
      });
      if (!toAdd.length) {
        const reason = skipped.some((s) => s.reason === 'in_volume') ? 'already_in_volume' : 'already_in_cart';
        return { added: [], skipped, message: t(reason) };
      }
      await this.request('cart/add.js', { items: toAdd });
      await this.get();
      return { added: toAdd, skipped, message: skipped.length ? t('some_skipped', { count: skipped.length }) : '' };
    } finally {
      this.pending = false;
    }
  },
  async change(key, quantity) { this.state = await this.request('cart/change.js', { id: key, quantity }); this.emit(); return this.state; },
  async removeVariants(variantIds) {
    const updates = {};
    variantIds.forEach((id) => { updates[id] = 0; });
    this.state = await this.request('cart/update.js', { updates });
    this.emit();
    return this.state;
  },
  async setDiscounts(codes) {
    this.state = await this.request('cart/update.js', { discount: codes.filter(Boolean).join(',') });
    this.emit();
    return this.state;
  }
};
window.CCPCart = Cart;

function drawer() { return doc.querySelector('ccp-cart-drawer'); }
/** Shared add-to-cart flow for every purchase control: drawer opens with result or error. */
async function addAndOpen(items, button, statusEl) {
  if (button) { if (button.getAttribute('aria-busy') === 'true') return; button.setAttribute('aria-busy', 'true'); button.disabled = true; }
  if (statusEl) { statusEl.textContent = t('adding'); statusEl.className = 'ccp-msg'; }
  try {
    const result = await Cart.add(items);
    if (result.busy) return;
    const d = drawer();
    if (statusEl) {
      statusEl.textContent = result.added.length ? t('added') + (result.message ? ' ' + result.message : '') : result.message;
      statusEl.className = 'ccp-msg ' + (result.added.length ? 'ccp-msg--ok' : 'ccp-msg--error');
    }
    if (d) { d.render(Cart.state, result.message); d.open(button); }
    else if (result.added.length) { window.location.href = cfg().cartUrl || '/cart'; }
    return result;
  } catch (err) {
    if (statusEl) { statusEl.textContent = err.message || t('error_generic'); statusEl.className = 'ccp-msg ccp-msg--error'; }
    else { const d = drawer(); if (d) { d.render(Cart.state, err.message, true); d.open(button); } }
  } finally {
    if (button) { button.removeAttribute('aria-busy'); button.disabled = false; button.dispatchEvent(new CustomEvent('ccp:refresh', { bubbles: true })); }
  }
}

/* ---------------- Reveal on scroll ---------------- */
let revealObserver;
function initReveal(scope) {
  const els = [...(scope || doc).querySelectorAll('[data-ccp-reveal]:not([data-in])')];
  if (!els.length) return;
  if (!root.classList.contains('ccp-motion') || reduceMotion() || designMode() || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.setAttribute('data-in', ''));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.setAttribute('data-in', ''); revealObserver.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
  }
  els.forEach((el) => revealObserver.observe(el));
  // Safety net: content must never stay hidden (e.g. print, background tabs, odd viewports).
  setTimeout(() => els.forEach((el) => el.setAttribute('data-in', '')), 3500);
}

/* ---------------- Announcement ---------------- */
class CcpMarquee extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-ccp-pause]');
    this.onClick = () => {
      const paused = this.toggleAttribute('data-paused');
      this.btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.btn.setAttribute('aria-label', paused ? t('play_announcements') : t('pause_announcements'));
    };
    if (this.btn) this.btn.addEventListener('click', this.onClick);
  }
  disconnectedCallback() { if (this.btn) this.btn.removeEventListener('click', this.onClick); }
}

/* ---------------- Header & mobile menu ---------------- */
class CcpHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.ccp-header');
    this.menu = this.querySelector('.ccp-menu');
    this.toggle = this.querySelector('[data-ccp-menu-open]');
    this.onScroll = () => {
      const y = scrollTop();
      if (this.bar) this.bar.toggleAttribute('data-scrolled', y > 8);
      this.measure();
    };
    this.measure = () => { root.style.setProperty('--ccp-header-h', (this.offsetHeight || 64) + 'px'); };
    this.onKey = (e) => {
      if (!this.menu || !this.menu.hasAttribute('data-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else trapFocus(this.menu, e);
    };
    this.onClick = (e) => {
      const opener = e.target.closest('[data-ccp-menu-open]');
      const closer = e.target.closest('[data-ccp-menu-close]');
      const link = e.target.closest('.ccp-menu a');
      const cartBtn = e.target.closest('[data-ccp-cart-open]');
      if (opener) { e.preventDefault(); this.open(); }
      else if (closer) { e.preventDefault(); this.close(); }
      else if (link) { this.close(false); }
      if (cartBtn) { e.preventDefault(); const d = drawer(); if (d) d.open(cartBtn); else window.location.href = cartBtn.getAttribute('href') || '/cart'; }
    };
    this.onResize = () => { this.measure(); if (window.innerWidth >= 990 && this.menu && this.menu.hasAttribute('data-open')) this.close(false); };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    doc.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onResize);
    doc.addEventListener('keydown', this.onKey);
    this.addEventListener('click', this.onClick);
    this.onScroll();
    this.onCart = (e) => this.updateCount(e.detail.cart);
    doc.addEventListener('ccp:cart', this.onCart);
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll); doc.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onResize);
    doc.removeEventListener('keydown', this.onKey);
    doc.removeEventListener('ccp:cart', this.onCart);
    this.removeEventListener('click', this.onClick);
    if (this.menu && this.menu.hasAttribute('data-open')) unlockScroll();
  }
  updateCount(cart) {
    if (!cart) return;
    this.querySelectorAll('[data-ccp-cart-count]').forEach((el) => { const was = el.textContent; el.textContent = cart.item_count; el.hidden = !cart.item_count; if (was !== String(cart.item_count) && cart.item_count) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); } });
    this.querySelectorAll('[data-ccp-cart-label]').forEach((el) => { el.setAttribute('aria-label', t('cart_count', { count: cart.item_count })); });
  }
  open() {
    if (!this.menu || this.menu.hasAttribute('data-open')) return;
    this.lastFocus = doc.activeElement;
    this.menu.hidden = false;
    this.menu.removeAttribute('inert');
    void this.menu.offsetWidth; this.menu.setAttribute('data-open', '');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    lockScroll();
    const first = this.menu.querySelector(FOCUSABLE);
    setTimeout(() => first && first.focus(), 60);
  }
  close(restoreFocus = true) {
    if (!this.menu || !this.menu.hasAttribute('data-open')) return;
    this.menu.removeAttribute('data-open');
    this.menu.setAttribute('inert', '');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
    unlockScroll();
    if (restoreFocus && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  }
}

/* ---------------- Cart drawer ---------------- */
class CcpCartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.ccp-drawer__panel');
    this.bodyEl = this.querySelector('[data-ccp-drawer-body]');
    this.footEl = this.querySelector('[data-ccp-drawer-foot]');
    this.countEl = this.querySelector('[data-ccp-drawer-count]');
    const local = this.querySelector('script[data-ccp-drawer-config]');
    if (local) {
      try {
        const x = JSON.parse(local.textContent);
        const c = cfg();
        Object.keys(x.strings || {}).forEach((k) => { if (x.strings[k]) c.strings[k] = x.strings[k]; });
        ['emptyLink', 'drawerNote', 'showCodes', 'showUpgrade'].forEach((k) => { if (k in x) c[k] = x[k]; });
      } catch (e) { /* keep defaults */ }
    }
    this.onKey = (e) => {
      if (!this.hasAttribute('data-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else trapFocus(this.panel, e);
    };
    this.onClick = (e) => this.handleClick(e);
    this.onSubmit = (e) => this.handleSubmit(e);
    this.onCart = (e) => { if (e.detail && e.detail.cart && !this._rendering) this.render(e.detail.cart); };
    doc.addEventListener('keydown', this.onKey);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    doc.addEventListener('ccp:cart', this.onCart);
    // Keep header count and drawer correct after refresh / back-forward navigation.
    this.onShow = (e) => { if (e.persisted) Cart.get().catch(() => {}); };
    window.addEventListener('pageshow', this.onShow);
    Cart.get().catch(() => {});
  }
  disconnectedCallback() {
    doc.removeEventListener('keydown', this.onKey);
    doc.removeEventListener('ccp:cart', this.onCart);
    window.removeEventListener('pageshow', this.onShow);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    if (this.hasAttribute('data-open')) unlockScroll();
  }
  open(trigger) {
    if (this.hasAttribute('data-open')) return;
    this.lastFocus = trigger || doc.activeElement;
    this.removeAttribute('inert');
    this.hidden = false;
    void this.offsetWidth; this.setAttribute('data-open', '');
    lockScroll();
    doc.querySelectorAll('[data-ccp-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    if (!Cart.state) Cart.get().catch(() => {});
    setTimeout(() => { const c = this.querySelector('[data-ccp-drawer-close]'); if (c) c.focus(); }, 60);
  }
  close() {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    this.setAttribute('inert', '');
    unlockScroll();
    doc.querySelectorAll('[data-ccp-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (this.lastFocus && this.lastFocus.focus && doc.contains(this.lastFocus)) this.lastFocus.focus();
  }
  setMessage(text, isError) {
    const el = this.querySelector('[data-ccp-drawer-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'ccp-msg' + (text ? (isError ? ' ccp-msg--error' : ' ccp-msg--ok') : '');
  }
  async handleClick(e) {
    const btn = e.target.closest('button, a');
    if (!btn || !this.contains(btn)) return;
    if (btn.matches('[data-ccp-drawer-close]')) { e.preventDefault(); this.close(); return; }
    if (btn.matches('[data-ccp-drawer-link]')) { this.close(); return; }
    const key = btn.getAttribute('data-key');
    try {
      if (btn.matches('[data-remove]')) { btn.disabled = true; await Cart.change(key, 0); }
      else if (btn.matches('[data-qty]')) { btn.disabled = true; await Cart.change(key, Math.max(0, parseInt(btn.getAttribute('data-qty'), 10))); }
      else if (btn.matches('[data-remove-code]')) {
        btn.disabled = true;
        const codes = (Cart.state.discount_codes || []).map((d) => d.code).filter((c) => c !== btn.getAttribute('data-remove-code'));
        await Cart.setDiscounts(codes);
      } else if (btn.matches('[data-ccp-swap-volume]')) {
        e.preventDefault();
        btn.setAttribute('aria-busy', 'true'); btn.disabled = true;
        const caseIds = Cart.state.items.filter((i) => caseByVariant(i.variant_id)).map((i) => i.variant_id);
        await Cart.removeVariants(caseIds);
        await Cart.add([{ id: cfg().volume.variantId, quantity: 1 }]);
        this.render(Cart.state, t('volume_swapped'));
      } else if (btn.matches('[data-ccp-quick-add]')) {
        e.preventDefault();
        btn.setAttribute('aria-busy', 'true'); btn.disabled = true;
        const res = await Cart.add([{ id: btn.getAttribute('data-ccp-quick-add'), quantity: 1 }]);
        this.render(Cart.state, res && res.message ? res.message : t('added'));
      } else if (btn.matches('[data-ccp-remove-included]')) {
        e.preventDefault();
        btn.disabled = true;
        const caseIds = Cart.state.items.filter((i) => caseByVariant(i.variant_id)).map((i) => i.variant_id);
        await Cart.removeVariants(caseIds);
        this.render(Cart.state, t('duplicates_removed'));
      }
    } catch (err) {
      this.render(Cart.state, err.message || t('error_generic'), true);
    }
  }
  async handleSubmit(e) {
    const form = e.target.closest('[data-ccp-code-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input');
    const code = (input.value || '').trim();
    if (!code) return;
    const btn = form.querySelector('button');
    btn.setAttribute('aria-busy', 'true'); btn.disabled = true;
    try {
      const existing = (Cart.state.discount_codes || []).map((d) => d.code);
      await Cart.setDiscounts(existing.concat(code));
      const applied = (Cart.state.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      this.render(Cart.state, applied && applied.applicable ? t('code_applied') : t('code_not_applicable'), !(applied && applied.applicable));
    } catch (err) {
      this.render(Cart.state, err.message || t('error_generic'), true);
    }
  }
  /** Builds drawer markup from real cart JSON (no invented prices). */
  ladder(caseCount) {
    const c = cfg();
    const steps = [{ count: 1, price: c.cases.length ? c.cases[0].price : 0, label: '1' }].concat(c.tiers.map((t0) => ({ count: t0.count, label: String(t0.count), discount: t0.discount })));
    const top = steps[steps.length - 1].count;
    const nt = nextTier(caseCount);
    const bt = bestTier(caseCount);
    const single = c.cases.length ? c.cases[0].price : 0;
    const pct = Math.min(100, Math.round((caseCount / top) * 100));
    const marks = steps.map((st) => {
      const price = st.discount ? single * st.count - st.discount : single;
      return `<li class="${caseCount >= st.count ? 'is-on' : ''}" style="--x:${Math.round((st.count / top) * 100)}%"><span class="ccp-ladder__dot"></span><span class="ccp-ladder__n">${esc(st.label)}</span><span class="ccp-ladder__p">${esc(money(price))}</span></li>`;
    }).join('');
    let msg;
    if (nt) {
      const save = nt.discount - (bt ? bt.discount : 0);
      msg = esc(t('ladder_next', { count: nt.count - caseCount, tier: nt.count, save: money(save) }));
    } else msg = esc(t('best_tier_reached'));
    return `<div class="ccp-ladder"><p class="ccp-ladder__msg">${msg}</p>
      <div class="ccp-ladder__track" role="progressbar" aria-valuemin="0" aria-valuemax="${top}" aria-valuenow="${caseCount}" aria-label="${esc(t('your_volume'))}"><span class="ccp-ladder__fill" style="--p:${pct}%"></span><ol role="list">${marks}</ol></div>
      ${bt ? `<p class="ccp-ladder__ok">${esc(t('tier_applied', { tier: bt.label }))}</p>` : ''}</div>`;
  }
  volumeOffer(caseCount, caseTotal, caseLines) {
    const c = cfg();
    if (!c.volume || !c.volume.available) return '';
    const remaining = Math.max(0, c.cases.length - caseLines.length);
    if (caseLines.length && !remaining) return '';
    const extra = c.volume.price - caseTotal;
    const singles = (c.volume && c.volume.compareAt > c.volume.price ? c.volume.compareAt : c.cases.reduce((n, x) => n + x.price, 0));
    const per = c.cases.length ? Math.round(c.volume.price / c.cases.length) : 0;
    const covers = c.cases.slice(0, 10).map((x) => x.image ? `<img src="${esc(x.image)}" alt="" width="40" height="40" loading="lazy">` : '<span></span>').join('');
    const maths = caseCount ? `<div class="ccp-maths"><div><span>${esc(t('your_cases_now'))}</span><span>${esc(money(caseTotal))}</span></div>
        <div><span>${esc(t('complete_volume'))}</span><span>${esc(money(c.volume.price))}</span></div>
        <div><span>${extra > 0 ? esc(t('difference')) : esc(t('you_save'))}</span><span>${extra > 0 ? '+' : '−'}${esc(money(Math.abs(extra)))}</span></div></div>` : '';
    const btn = caseCount ? `<button type="button" class="ccp-btn ccp-btn--paper ccp-btn--block ccp-btn--sm" style="margin-top:12px" data-ccp-swap-volume>${esc(t('swap_volume'))}</button>`
      : `<button type="button" class="ccp-btn ccp-btn--paper ccp-btn--block ccp-btn--sm" style="margin-top:12px" data-ccp-quick-add="${esc(c.volume.variantId)}">${esc(t('add_volume', { count: c.cases.length, price: money(c.volume.price) }))}</button>`;
    return `<div class="ccp-upgrade ccp-upgrade--dark"><p class="ccp-upgrade__kicker">${esc(t('upgrade_kicker'))}</p>
      <p class="ccp-upgrade__title">${esc(t('upgrade_title', { count: c.cases.length, price: money(c.volume.price) }))}${singles > c.volume.price ? ` <s class="ccp-upgrade__was">${esc(money(singles))}</s>` : ''}</p>
      <p class="ccp-upgrade__body">${esc(t('upgrade_per', { per: money(per), was: money(singles) }))}</p>
      <div class="ccp-upgrade__covers" aria-hidden="true">${covers}</div>${maths}${btn}</div>`;
  }
  /** Multi-bundle offer: only when there are several bundles and bundle tiers backed by a Shopify discount. */
  bundleLadder(items) {
    const c = cfg();
    const bundles = c.bundles || [], tiers = (c.bundleTiers || []).filter((x) => x.count > 1 && x.percent > 0).sort((a, b) => a.count - b.count);
    if (bundles.length < 2 || !tiers.length) return '';
    const inCart = items.filter((i) => bundleByVariant(i.variant_id));
    if (!inCart.length) return '';
    const n = inCart.reduce((k, i) => k + i.quantity, 0);
    const next = tiers.find((x) => x.count > n);
    const reached = [...tiers].reverse().find((x) => x.count <= n);
    const others = bundles.filter((b) => b.available && !inCart.some((i) => String(i.variant_id) === String(b.variantId))).slice(0, 2);
    let html = `<div class="ccp-bladder"><p class="ccp-bladder__msg">${esc(next ? t('bundle_next', { count: next.count - n, percent: next.percent }) : t('bundle_best'))}</p>`;
    if (reached) html += `<p class="ccp-ladder__ok">${esc(t('bundle_tier_applied', { label: reached.label || reached.percent + '%' }))}</p>`;
    if (next && others.length) html += '<ul role="list">' + others.map((b) => `<li><span class="ccp-line__thumb">${b.image ? `<img src="${esc(b.image)}" alt="" width="40" height="48" loading="lazy">` : ''}</span><span class="ccp-line__info"><a class="ccp-line__title" href="${esc(b.url)}" data-ccp-drawer-link>${esc(b.title)}</a></span><button type="button" class="ccp-btn ccp-btn--outline ccp-btn--sm" data-ccp-quick-add="${esc(b.variantId)}">+ ${esc(money(b.price))}</button></li>`).join('') + '</ul>';
    return html + '</div>';
  }
  volumeMini(caseCount) {
    const c = cfg();
    if (!c.volume || !c.volume.available) return '';
    const per = c.cases.length ? Math.round(c.volume.price / c.cases.length) : 0;
    const singles = (c.volume && c.volume.compareAt > c.volume.price ? c.volume.compareAt : c.cases.reduce((n, x) => n + x.price, 0));
    return `<div class="ccp-vmini"><span>${esc(t('upgrade_title', { count: c.cases.length, price: money(c.volume.price) }))}${singles > c.volume.price ? ` <s>${esc(money(singles))}</s>` : ''} · ${esc(t('per_night', { price: money(per) }))}</span>
      <button type="button" class="ccp-btn ccp-btn--ink ccp-btn--sm" ${caseCount ? 'data-ccp-swap-volume' : `data-ccp-quick-add="${esc(c.volume.variantId)}"`}>${esc(t('drawer_upgrade_short'))}</button></div>`;
  }
  suggestions(exclude, max) {
    const c = cfg();
    const list = c.cases.filter((x) => x.available && !exclude.includes(String(x.variantId))).slice(0, max);
    if (!list.length) return '';
    return `<div class="ccp-suggest"><p class="ccp-label">${esc(t('suggest_title'))}</p><ul role="list">` + list.map((x) => `<li>
      <span class="ccp-line__thumb" style="--thumb:${esc(x.colour)}">${x.image ? `<img src="${esc(x.image)}" alt="" width="40" height="48" loading="lazy">` : ''}</span>
      <span class="ccp-line__info"><a class="ccp-line__title" href="${esc(x.url)}" data-ccp-drawer-link>${esc(x.title)}</a>${x.hook ? `<span class="ccp-line__meta">${esc(x.hook)}</span>` : ''}</span>
      <button type="button" class="ccp-btn ccp-btn--outline ccp-btn--sm" data-ccp-quick-add="${esc(x.variantId)}">+ ${esc(money(x.price))}</button></li>`).join('') + '</ul></div>';
  }
  render(cart, message, isError) {
    if (!cart || !this.bodyEl) return;
    this._rendering = true;
    const c = cfg();
    const s = (k, v) => esc(t(k, v));
    if (this.countEl) this.countEl.textContent = cart.item_count;
    const items = cart.items || [];
    let html = '<p class="ccp-msg" data-ccp-drawer-msg role="status" aria-live="polite"></p>';
    if (!items.length) {
      html += `<div class="ccp-drawer__empty"><h3>${s('empty_title')}</h3><p>${s('empty_body')}</p>` +
        (c.emptyLink ? `<a class="ccp-btn ccp-btn--outline ccp-btn--sm" href="${esc(c.emptyLink)}" data-ccp-drawer-link>${s('empty_cta')}</a>` : '') + '</div>';
      html += this.volumeOffer(0, 0, []);
      if (c.experienceUrl) html += `<p class="ccp-drawer__alt"><a class="ccp-link" href="${esc(c.experienceUrl + (c.experienceUrl.includes('?') ? '&' : '?') + 'pkg=season')}" data-ccp-drawer-link>${s('drawer_choose')}</a></p>`;
      this.bodyEl.innerHTML = html;
      if (this.footEl) this.footEl.hidden = true;
      this.setMessage(message, isError);
      this._rendering = false;
      return;
    }
    const caseLines0 = items.filter((i) => caseByVariant(i.variant_id));
    const caseCount0 = caseLines0.reduce((n, i) => n + i.quantity, 0);
    const volumeLine0 = c.volume ? items.find((i) => isVolumeVariant(i.variant_id)) : null;
    if (!volumeLine0 && c.tiers.length) html += this.ladder(caseCount0);
    else if (volumeLine0 && c.cases.length) html += `<div class="ccp-unlocked"><span class="ccp-unlocked__icon" aria-hidden="true">✓</span><span>${s('best_unlocked', { per: money(Math.round(volumeLine0.final_line_price / c.cases.length)) })}</span></div>`;
    html += '<ul role="list" class="ccp-drawer__lines">';
    items.forEach((item) => {
      const cs = caseByVariant(item.variant_id);
      const vol = isVolumeVariant(item.variant_id);
      const bd = bundleByVariant(item.variant_id);
      const limited = c.limitQty && (cs || vol || bd);
      const bdSave = bd && bd.value ? bd.value * item.quantity - item.final_line_price : 0;
      const thumb = item.image ? `<img src="${esc(item.image + (item.image.includes('?') ? '&' : '?') + 'width=120')}" alt="" width="44" height="54" loading="lazy">` : esc(cs ? cs.ref : '');
      const allocs = (item.line_level_discount_allocations || []).filter((a) => a.amount > 0);
      const meta = cs ? [cs.ref, cs.meta].filter(Boolean).join(' · ') : (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : (vol ? t('complete_volume') : ''));
      html += `<li class="ccp-line">
        <span class="ccp-line__thumb" style="--thumb:${esc(cs ? cs.colour : '#0C0C0C')}">${thumb}</span>
        <span class="ccp-line__info">
          <a class="ccp-line__title" href="${esc(item.url)}" data-ccp-drawer-link>${esc(item.product_title)}</a>
          ${meta ? `<span class="ccp-line__meta">${esc(meta)}</span>` : ''}
          ${bdSave > 0 ? `<span class="ccp-line__save">${s('bundle_worth', { value: money(bd.value * item.quantity), save: money(bdSave) })}</span>` : ''}
          ${allocs.map((a) => `<span class="ccp-line__disc">${esc(a.discount_application.title)} (−${esc(money(a.amount))})</span>`).join('')}
          ${limited ? '' : `<span class="ccp-qty" role="group" aria-label="${s('quantity_for', { title: item.product_title })}">
              <button type="button" data-qty="${item.quantity - 1}" data-key="${esc(item.key)}" aria-label="${s('decrease')}">−</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-qty="${item.quantity + 1}" data-key="${esc(item.key)}" aria-label="${s('increase')}">+</button></span>`}
        </span>
        <span class="ccp-line__side">
          <span class="ccp-line__price">${esc(money(item.final_line_price))}</span>
          ${item.original_line_price > item.final_line_price ? `<span class="ccp-line__was"><span class="ccp-visually-hidden">${s('regular_price')}</span>${esc(money(item.original_line_price))}</span>` : (bdSave > 0 ? `<span class="ccp-line__was"><span class="ccp-visually-hidden">${s('regular_price')}</span>${esc(money(bd.value * item.quantity))}</span>` : '')}
          <button type="button" class="ccp-line__remove" data-remove data-key="${esc(item.key)}" aria-label="${s('remove_item', { title: item.product_title })}">${s('remove')}</button>
        </span>
      </li>`;
    });
    html += '</ul>';

    const caseLines = items.filter((i) => caseByVariant(i.variant_id));
    const caseCount = caseLines.reduce((n, i) => n + i.quantity, 0);
    const volumeLine = c.volume ? items.find((i) => isVolumeVariant(i.variant_id)) : null;

    // Duplicate warning: complete volume + singles.
    if (volumeLine && caseLines.length && c.limitQty) {
      html += `<div class="ccp-upgrade"><p class="ccp-upgrade__kicker">${s('duplicate_kicker')}</p>
        <p class="ccp-upgrade__body">${s('duplicate_body', { count: caseLines.length })}</p>
        <button type="button" class="ccp-btn ccp-btn--ink ccp-btn--block ccp-btn--sm" style="margin-top:11px" data-ccp-remove-included>${s('remove_included')}</button></div>`;
    }
    // Upgrade + suggestions (real prices only).
    if (!volumeLine && c.showUpgrade) {
      const caseTotal = caseLines.reduce((n, i) => n + i.final_line_price, 0);
      // One offer at a time: below the first tier, help reach it; from there on, the complete volume.
      const nt = nextTier(caseCount);
      if (caseCount && nt && caseCount < (c.tiers[0] ? c.tiers[0].count : 3)) {
        html += this.suggestions(caseLines.map((i) => String(i.variant_id)), nt.count - caseCount);
        html += this.volumeMini(caseCount);
      } else html += this.volumeOffer(caseCount, caseTotal, caseLines);
    }
    html += this.bundleLadder(items);
    this.bodyEl.innerHTML = html;

    // Footer totals from Shopify's own numbers.
    if (this.footEl) {
      this.footEl.hidden = false;
      const discountTotal = cart.total_discount || 0;
      const codes = cart.discount_codes || [];
      const cartLevel = (cart.cart_level_discount_applications || []).filter((d) => d.total_allocated_amount > 0);
      let foot = '';
      foot += `<div class="ccp-sumrow"><span>${s('subtotal')}</span><span>${esc(money(cart.original_total_price))}</span></div>`;
      cartLevel.forEach((d) => { foot += `<div class="ccp-sumrow"><span>${esc(d.title)}</span><span>−${esc(money(d.total_allocated_amount))}</span></div>`; });
      if (discountTotal > 0) foot += `<div class="ccp-sumrow ccp-sumrow--keep"><span>${s('you_keep')}</span><span>−${esc(money(discountTotal))}</span></div>`;
      const bundleSave = items.reduce((n, it) => { const b = bundleByVariant(it.variant_id); return n + (b && b.value ? Math.max(0, b.value * it.quantity - it.original_line_price) : 0); }, 0);
      const saved = bundleSave + discountTotal;
      if (saved > 0) foot = `<div class="ccp-savings" role="status">${s('order_savings', { amount: money(saved) })}</div>` + foot;
      foot += `<div class="ccp-sumrow ccp-sumrow--total" style="margin-top:10px"><span>${s('total')}</span><span>${esc(money(cart.total_price))}</span></div>`;
      foot += `<p class="ccp-drawer__note">${esc(c.drawerNote || '')} ${c.taxesIncluded ? s('taxes_included') : s('taxes_at_checkout')}</p>`;
      if (c.showCodes) {
        foot += `<form class="ccp-code" data-ccp-code-form novalidate><label class="ccp-visually-hidden" for="ccp-code-input">${s('discount_code')}</label>
          <input id="ccp-code-input" type="text" autocomplete="off" placeholder="${s('discount_code')}"><button type="submit" class="ccp-btn ccp-btn--outline ccp-btn--sm">${s('apply')}</button></form>`;
        if (codes.length) foot += '<div class="ccp-chipline">' + codes.map((d) => `<span class="ccp-codechip">${esc(d.code)}${d.applicable ? '' : ' · ' + s('not_applicable')}<button type="button" data-remove-code="${esc(d.code)}" aria-label="${s('remove_code', { code: d.code })}">×</button></span>`).join('') + '</div>';
      }
      foot += `<a class="ccp-btn ccp-btn--ink ccp-btn--block" style="margin-top:13px" href="${esc(c.checkoutUrl || '/checkout')}">${s('checkout')}</a>`;
      this.footEl.innerHTML = foot;
    }
    this.setMessage(message, isError);
    this._rendering = false;
  }
}

/* ---------------- Generic add button (data-variants="id,id") ---------------- */
class CcpAddButton extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('button');
    this.onClick = (e) => {
      e.preventDefault();
      const ids = (this.getAttribute('data-variants') || '').split(',').filter(Boolean);
      if (!ids.length) return;
      addAndOpen(ids.map((id) => ({ id, quantity: 1 })), this.btn, this.querySelector('[data-ccp-status]'));
    };
    if (this.btn) this.btn.addEventListener('click', this.onClick);
  }
  disconnectedCallback() { if (this.btn) this.btn.removeEventListener('click', this.onClick); }
}

/* ---------------- Horizontal scroller ---------------- */
class CcpScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ccp-track]');
    this.prev = this.querySelector('[data-ccp-prev]');
    this.next = this.querySelector('[data-ccp-next]');
    if (!this.track) return;
    this.update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      const nav = this.querySelector('.ccp-scroller__nav');
      if (nav) nav.hidden = max <= 0;
    };
    this.step = (dir) => {
      const item = this.track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width + 14 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: dir * w, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };
    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    if (this.prev) this.prev.addEventListener('click', this.onPrev);
    if (this.next) this.next.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.ro = new ResizeObserver(this.update);
    this.ro.observe(this.track);
    this.update();
  }
  disconnectedCallback() {
    if (!this.track) return;
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    this.track.removeEventListener('scroll', this.update);
    if (this.ro) this.ro.disconnect();
  }
}

/* ---------------- Product gallery ----------------
   Slides carry data-set tags ("vol", "own", "cover") and an optional case variant id. Purchase controls call
   filter() so the gallery always shows what is actually selected (the complete volume, a set of cases, one case). */
const hasSet = (el, tag) => (' ' + (el.getAttribute('data-set') || '') + ' ').includes(' ' + tag + ' ');
class CcpGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-ccp-viewport]');
    this.all = [...this.querySelectorAll('[data-ccp-slide]')];
    this.allThumbs = [...this.querySelectorAll('[data-ccp-thumb]')];
    this.counter = this.querySelector('[data-ccp-counter]');
    this.live = this.querySelector('[data-ccp-gallery-live]');
    this.ctx = this.querySelector('[data-ccp-gallery-ctx]');
    this.index = 0;
    if (!this.viewport || !this.all.length) return;
    this.collect();
    this.onThumb = (e) => { const b = e.target.closest('[data-ccp-thumb]'); if (b) this.go(this.slides.indexOf(this.all[parseInt(b.getAttribute('data-index'), 10)])); };
    this.onPrev = () => this.go(this.index - 1);
    this.onNext = () => this.go(this.index + 1);
    this.onKey = (e) => { if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); } if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); } };
    this.onScroll = () => {
      clearTimeout(this._st);
      this._st = setTimeout(() => {
        const i = Math.round(this.viewport.scrollLeft / Math.max(1, this.viewport.clientWidth));
        if (i !== this.index && i < this.slides.length) this.setActive(i, false);
      }, 60);
    };
    this.onMedia = (e) => { const id = e.detail && e.detail.mediaId; const i = this.slides.findIndex((s) => s.getAttribute('data-media-id') === String(id)); if (i > -1) this.go(i); };
    this.addEventListener('click', this.onThumb);
    this.prevBtn = this.querySelector('[data-ccp-prev]'); this.nextBtn = this.querySelector('[data-ccp-next]');
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
    this.viewport.addEventListener('keydown', this.onKey);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    doc.addEventListener('ccp:media', this.onMedia);
    this.setActive(0, false);
  }
  disconnectedCallback() {
    if (!this.viewport) return;
    this.removeEventListener('click', this.onThumb);
    this.viewport.removeEventListener('keydown', this.onKey);
    this.viewport.removeEventListener('scroll', this.onScroll);
    doc.removeEventListener('ccp:media', this.onMedia);
  }
  collect() {
    this.slides = this.all.filter((s) => !s.hidden);
    const many = this.slides.length > 1;
    [this.prevBtn, this.nextBtn, this.counter].forEach((el) => { if (el) el.hidden = !many; });
    const wrap = this.querySelector('.ccp-gallery__thumbs');
    if (wrap) wrap.hidden = !many;
  }
  /** Show only slides for which match(slide) is true; label describes the selection. */
  filter(match, label) {
    const key = label + '|' + this.all.map((s) => (match(s) ? 1 : 0)).join('');
    if (key === this._key) return;
    this._key = key;
    let any = false;
    this.all.forEach((s) => { const on = !!match(s); s.hidden = !on; if (on) any = true; });
    if (!any) this.all.forEach((s) => { s.hidden = false; });
    this.allThumbs.forEach((b) => { const s = this.all[parseInt(b.getAttribute('data-index'), 10)]; b.hidden = !s || s.hidden; });
    this.collect();
    this.slides.slice(0, 2).forEach((sl) => { const im = sl.querySelector('img'); if (im) im.loading = 'eager'; });
    if (this.ctx && label) this.ctx.textContent = label;
    this.viewport.scrollLeft = 0;
    this.setActive(0, false);
  }
  go(i) {
    const n = this.slides.length;
    if (!n || i < -n) return;
    const idx = (i + n) % n;
    this.viewport.scrollTo({ left: idx * this.viewport.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
    this.setActive(idx, true);
  }
  setActive(i, announce) {
    this.index = i;
    const cur = this.slides[i];
    this.allThumbs.forEach((b) => b.setAttribute('aria-current', this.all[parseInt(b.getAttribute('data-index'), 10)] === cur ? 'true' : 'false'));
    this.slides.forEach((s, k) => s.setAttribute('aria-hidden', k === i ? 'false' : 'true'));
    const label = t('gallery_counter', { current: String(i + 1).padStart(2, '0'), total: String(this.slides.length).padStart(2, '0') });
    if (this.counter) this.counter.textContent = label;
    if (announce && this.live) this.live.textContent = t('image_of', { current: i + 1, total: this.slides.length });
    const th = this.allThumbs.find((b) => b.getAttribute('aria-current') === 'true');
    if (th && announce) { const w = th.parentElement; w.scrollTo({ left: th.offsetLeft - w.clientWidth / 2 + th.offsetWidth / 2, behavior: 'auto' }); }
  }
}

/* ---------------- Product form (variants, package tiers, sticky sync) ---------------- */
class CcpProductForm extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[data-ccp-product]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.status = this.querySelector('[data-ccp-status]');
    this.picker = this.querySelector('[data-ccp-casepicker]');
    this.onChange = (e) => this.handleChange(e);
    this.onSubmit = (e) => { e.preventDefault(); this.submit(e.submitter || this.querySelector('[data-ccp-submit]')); };
    this.addEventListener('change', this.onChange);
    this.form = this.querySelector('form');
    if (this.form) this.form.addEventListener('submit', this.onSubmit);
    this.onExternal = (e) => { if (e.detail && e.detail.formId === this.id) this.submit(e.detail.button); };
    doc.addEventListener('ccp:submit', this.onExternal);
    this.onCart = () => this.refresh();
    doc.addEventListener('ccp:cart', this.onCart);
    this.refresh();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    doc.removeEventListener('ccp:submit', this.onExternal);
    doc.removeEventListener('ccp:cart', this.onCart);
  }
  get variant() {
    const d = this.data;
    const selected = [...this.querySelectorAll('[data-ccp-option]:checked')].map((i) => i.value);
    if (!selected.length || d.variants.length === 1) {
      const idInput = this.querySelector('[data-ccp-variant-id]');
      return d.variants.find((v) => String(v.id) === String(idInput && idInput.value)) || d.variants[0];
    }
    return d.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
  }
  get pkg() { const r = this.querySelector('input[name="ccp-package"]:checked'); return r ? r.value : 'single'; }
  pickedCases() { return this.picker ? [...this.picker.querySelectorAll('input[type="checkbox"]:checked')].map((i) => i.value) : []; }
  handleChange(e) {
    if (e.target.matches('[data-ccp-option]')) {
      const v = this.variant;
      const idInput = this.querySelector('[data-ccp-variant-id]');
      if (v && idInput) idInput.value = v.id;
      if (v && v.featured_media_id) doc.dispatchEvent(new CustomEvent('ccp:media', { detail: { mediaId: v.featured_media_id } }));
      if (v && this.data.updateUrl) { const u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState(null, '', u.toString()); }
    }
    if (e.target.matches('input[name="ccp-package"]') && this.picker) {
      const need = this.tierCount();
      this.picker.hidden = !(need > 1);
      if (need > 1) { const first = this.picker.querySelector('input:not(:disabled)'); if (first && e.isTrusted) setTimeout(() => this.picker.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' }), 30); }
    }
    this.refresh();
  }
  tierCount() { const p = this.pkg; return /^\d+$/.test(p) ? parseInt(p, 10) : 0; }
  /** Compute the real selection: lines to add, subtotal, enforced tier discount, validity. */
  compute() {
    const d = this.data, c = cfg();
    const v = this.variant;
    const qtyInput = this.querySelector('[data-ccp-qty]');
    const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    const res = { lines: [], subtotal: 0, discount: 0, compareAt: 0, valid: true, reason: '', label: '' };
    if (!v) { res.valid = false; res.reason = t('unavailable'); return res; }
    if (!v.available) { res.valid = false; res.reason = t('sold_out'); }
    if (d.blockZeroPrice && v.price <= 0) { res.valid = false; res.reason = t('not_for_sale_yet'); }
    const p = this.pkg;
    if (p === 'volume' && c.volume) {
      res.lines = [{ id: c.volume.variantId, quantity: 1 }];
      res.subtotal = c.volume.price;
      res.compareAt = (c.volume && c.volume.compareAt > c.volume.price ? c.volume.compareAt : c.cases.reduce((n, x) => n + x.price, 0));
      res.valid = c.volume.available && c.volume.price > 0;
      if (!res.valid) res.reason = t('unavailable');
      res.label = t('add_volume', { count: c.cases.length, price: money(c.volume.price) });
      return res;
    }
    const need = this.tierCount();
    if (need > 1) {
      const picked = this.pickedCases().filter((id) => String(id) !== String(v.id));
      const ids = [String(v.id)].concat(picked);
      res.lines = ids.map((id) => ({ id, quantity: 1 }));
      res.subtotal = ids.reduce((n, id) => { const cs = caseByVariant(id); return n + (cs ? cs.price : v.price); }, 0);
      const tier = bestTier(ids.length);
      res.discount = tier ? tier.discount : 0;
      res.compareAt = res.subtotal;
      if (ids.length !== need) { res.valid = false; res.reason = t('choose_more', { count: need - ids.length }); res.remaining = need - ids.length; }
      res.label = t('add_n_cases', { count: need, price: money(res.subtotal - res.discount) });
      return res;
    }
    res.lines = [{ id: v.id, quantity: c.limitQty && d.isCase ? 1 : qty }];
    res.subtotal = v.price * res.lines[0].quantity;
    res.compareAt = v.compare_at_price > v.price ? v.compare_at_price * res.lines[0].quantity : 0;
    res.label = d.buttonLabel;
    return res;
  }
  refresh() {
    if (!this.data) return;
    const r = this.compute();
    const total = r.subtotal - r.discount;
    const was = r.discount > 0 ? r.subtotal : (r.compareAt > total ? r.compareAt : 0);
    const save = was > total ? was - total : 0;
    const set = (sel, fn) => doc.querySelectorAll(`[data-ccp-for="${this.id}"] ${sel}, #${this.id} ${sel}`).forEach(fn);
    set('[data-ccp-price]', (el) => { el.textContent = money(total); });
    set('[data-ccp-was]', (el) => { el.textContent = was ? money(was) : ''; el.hidden = !was; });
    set('[data-ccp-save]', (el) => { el.textContent = save ? t('save_amount', { amount: money(save), percent: Math.round((save / was) * 100) }) : ''; });
    set('[data-ccp-sub]', (el) => {
      const n = r.lines.length;
      el.textContent = n > 1 ? t('per_file', { count: n, price: money(Math.round(total / n)) }) : (this.pkg === 'volume' && cfg().cases.length ? t('per_file', { count: cfg().cases.length, price: money(Math.round(total / cfg().cases.length)) }) : el.getAttribute('data-default') || '');
    });
    if (this.picker) {
      const head = this.picker.querySelector('[data-ccp-picker-count]');
      const need = this.tierCount();
      const count = 1 + this.pickedCases().filter((id) => String(id) !== String(this.variant && this.variant.id)).length;
      if (head) head.textContent = t('picked_of', { count, total: need });
      this.picker.querySelectorAll('input[type="checkbox"]').forEach((i) => { if (!i.checked) i.disabled = i.hasAttribute('data-unavailable') || count >= need; });
    }
    const cart = Cart.state;
    let inCart = false;
    if (cart && cfg().limitQty && r.lines.length === 1 && (this.data.isCase || this.pkg === 'volume')) inCart = !!Cart.lineFor(r.lines[0].id);
    set('[data-ccp-submit]', (btn) => {
      const label = btn.querySelector('[data-ccp-label]') || btn;
      btn.disabled = !r.valid && !(r.remaining && btn.hasAttribute('data-ccp-sticky'));
      label.textContent = !r.valid ? (r.remaining && btn.hasAttribute('data-ccp-sticky') ? t('choose_cases') : r.reason) : (inCart ? t('in_cart_view') : r.label);
      btn.toggleAttribute('data-in-cart', inCart);
    });
    this._last = r;
    this.syncGallery();
  }
  syncGallery() {
    const g = this.closest('.ccp-pdp') && this.closest('.ccp-pdp').querySelector('ccp-gallery');
    if (!g || !g.filter || !g.ctx) return;
    const own = String(this.variant ? this.variant.id : '');
    const p = this.pkg, need = this.tierCount();
    if (p === 'volume') g.filter((s) => hasSet(s, 'vol') || hasSet(s, 'cover'), t('ctx_volume', { count: cfg().cases.length }));
    else if (need > 1) {
      const ids = [own].concat(this.pickedCases().map(String));
      g.filter((s) => hasSet(s, 'cover') && ids.includes(s.getAttribute('data-variant')), t('ctx_tier', { picked: ids.length, count: need }));
    } else g.filter((s) => hasSet(s, 'own'), t('ctx_single'));
  }
  async submit(button) {
    const r = this.compute();
    if (button && button.hasAttribute('data-in-cart')) { const d = drawer(); if (d) { d.render(Cart.state); d.open(button); } return; }
    if (!r.valid) {
      if (r.remaining && this.picker) { this.picker.hidden = false; this.picker.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' }); const f = this.picker.querySelector('input:not(:disabled):not(:checked)'); if (f) f.focus(); }
      if (this.status) { this.status.textContent = r.reason; this.status.className = 'ccp-msg ccp-msg--error'; }
      return;
    }
    await addAndOpen(r.lines, button, this.status);
    this.refresh();
  }
}

/* ---------------- Experience page: complete archive by default, or a season / one case chosen from the chapters ---------------- */
class CcpExperience extends HTMLElement {
  connectedCallback() {
    this.picker = this.querySelector('[data-ccp-exp-picker]');
    this.status = this.querySelector('[data-ccp-status]');
    this.nudge = this.querySelector('[data-ccp-exp-nudge]');
    this.season = parseInt(this.getAttribute('data-season'), 10) || 5;
    this.onChange = (e) => this.handleChange(e);
    this.onClick = (e) => { const b = e.target.closest('[data-ccp-submit]'); if (b && this.contains(b)) { e.preventDefault(); this.submit(b); } };
    this.onExternal = (e) => { if (e.detail && e.detail.formId === this.id) this.submit(e.detail.button); };
    this.onCart = () => this.refresh();
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    doc.addEventListener('ccp:submit', this.onExternal);
    doc.addEventListener('ccp:cart', this.onCart);
    this.applyUrl();
    this.refresh();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    doc.removeEventListener('ccp:submit', this.onExternal);
    doc.removeEventListener('ccp:cart', this.onCart);
  }
  /** ?pkg=volume|season|trial&case=<variant id or handle> — used by the offer ladder and case pages. */
  applyUrl() {
    let q;
    try { q = new URL(window.location.href).searchParams; } catch (e) { return; }
    const pkg = q.get('pkg');
    const radio = pkg && this.querySelector(`input[name="ccp-exp-pkg"][value="${CSS.escape(pkg)}"]`);
    if (radio) radio.checked = true;
    const cs = q.get('case') || this.getAttribute('data-ccp-case');
    if (cs && this.picker) {
      const box = [...this.picker.querySelectorAll('input[type="checkbox"]')].find((i) => i.value === cs || i.getAttribute('data-handle') === cs);
      if (box && !box.disabled) box.checked = true;
    }
    this.showPicker(false);
  }
  get pkg() { const r = this.querySelector('input[name="ccp-exp-pkg"]:checked'); return r ? r.value : 'volume'; }
  get need() { return this.pkg === 'season' ? this.season : this.pkg === 'trial' ? 1 : 0; }
  boxes() { return this.picker ? [...this.picker.querySelectorAll('input[type="checkbox"]')] : []; }
  picked() { return this.boxes().filter((i) => i.checked).map((i) => i.value); }
  showPicker(scroll) {
    if (!this.picker) return;
    const need = this.need;
    this.picker.hidden = need === 0;
    // Keep only as many picks as the option allows (most recent first for the single case).
    const on = this.boxes().filter((i) => i.checked);
    if (need && on.length > need) on.slice(need).forEach((i) => { i.checked = false; });
    if (need && scroll) setTimeout(() => this.picker.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' }), 30);
  }
  handleChange(e) {
    if (e.target.matches('input[name="ccp-exp-pkg"]')) this.showPicker(e.isTrusted);
    else if (e.target.matches('[data-ccp-exp-picker] input[type="checkbox"]') && this.need === 1 && e.target.checked) {
      this.boxes().forEach((i) => { if (i !== e.target) i.checked = false; });
    }
    this.refresh();
    // Selection complete: add it and open the cart straight away (only on a real click, never on page load).
    if (e.isTrusted && e.target.matches('[data-ccp-exp-picker] input[type="checkbox"]') && e.target.checked && this.need && this.picked().length === this.need) {
      clearTimeout(this._auto);
      this._auto = setTimeout(() => {
        const btn = doc.querySelector(`#${this.id} [data-ccp-submit]`) || doc.querySelector(`[data-ccp-for="${this.id}"] [data-ccp-submit]`);
        if (this.picked().length === this.need) this.submit(btn);
      }, 450);
    }
  }
  compute() {
    const c = cfg();
    const single = c.cases.length ? c.cases[0].price : 0;
    const res = { lines: [], total: 0, was: 0, valid: true, reason: '', label: '', per: 0, count: 0 };
    if (this.pkg === 'volume') {
      if (!c.volume) { res.valid = false; return res; }
      res.lines = [{ id: c.volume.variantId, quantity: 1 }];
      res.total = c.volume.price;
      res.was = (c.volume && c.volume.compareAt > c.volume.price ? c.volume.compareAt : c.cases.reduce((n, x) => n + x.price, 0));
      res.count = c.cases.length;
      res.per = res.count ? Math.round(res.total / res.count) : 0;
      res.valid = c.volume.available && c.volume.price > 0;
      res.label = t('exp_add_volume', { count: res.count, price: money(res.total) });
      const k = single ? Math.floor(res.total / single) + 1 : 0;
      res.nudge = single ? t('exp_nudge_volume', { per: money(res.per), k: numWord(k) }) : '';
      return res;
    }
    const need = this.need;
    const ids = this.picked();
    const sub = ids.reduce((n, id) => { const cs = caseByVariant(id); return n + (cs ? cs.price : 0); }, 0);
    const tier = bestTier(ids.length);
    res.lines = ids.map((id) => ({ id, quantity: 1 }));
    res.count = ids.length;
    res.total = sub - (tier ? tier.discount : 0);
    res.was = tier ? sub : 0;
    res.per = ids.length ? Math.round(res.total / ids.length) : 0;
    if (ids.length < need) {
      res.valid = false;
      res.remaining = need - ids.length;
      res.reason = need === 1 ? t('exp_choose_one') : t('exp_choose', { count: res.remaining });
      // Show the price of the finished selection so the number never jumps unexpectedly.
      const full = single * need, ft = bestTier(need);
      res.total = full - (ft ? ft.discount : 0);
      res.was = ft ? full : 0;
      res.nudge = need === 1 ? t('exp_nudge_trial') : t('exp_nudge_more', { count: res.remaining });
      return res;
    }
    res.label = need === 1 ? t('exp_add_one', { price: money(res.total) }) : t('exp_add_n', { count: ids.length, price: money(res.total) });
    const vol = c.volume;
    if (need > 1 && vol && vol.available && vol.price > res.total) res.nudge = t('exp_nudge_upgrade', { diff: money(vol.price - res.total), total: c.cases.length });
    else if (need === 1) {
      const st = c.tiers.find((x) => x.count === this.season) || c.tiers[c.tiers.length - 1];
      if (st) { const p = single * st.count - st.discount; res.nudge = t('exp_nudge_trial_done', { count: st.count, price: money(p), per: money(Math.round(p / st.count)) }); }
    }
    return res;
  }
  refresh() {
    const r = this.compute();
    const set = (sel, fn) => doc.querySelectorAll(`[data-ccp-for="${this.id}"] ${sel}, #${this.id} ${sel}`).forEach(fn);
    set('[data-ccp-price]', (el) => { el.textContent = money(r.total); });
    set('[data-ccp-was]', (el) => { el.textContent = r.was > r.total ? money(r.was) : ''; el.hidden = !(r.was > r.total); });
    set('[data-ccp-save]', (el) => { el.textContent = r.was > r.total ? t('save_amount', { amount: money(r.was - r.total), percent: Math.round(((r.was - r.total) / r.was) * 100) }) : ''; });
    if (this.nudge) this.nudge.textContent = r.nudge || '';
    const need = this.need, count = this.picked().length;
    const head = this.querySelector('[data-ccp-exp-count]');
    if (head) head.textContent = need ? t('picked_of', { count, total: need }) : '';
    this.boxes().forEach((i) => { if (!i.checked) i.disabled = i.hasAttribute('data-unavailable') || (need > 1 && count >= need); });
    // One copy per case: mark what the cart already holds.
    const inCart = !!(Cart.state && r.valid && r.lines.length && r.lines.every((l) => Cart.lineFor(l.id)));
    set('[data-ccp-submit]', (btn) => {
      const label = btn.querySelector('[data-ccp-label]') || btn;
      btn.disabled = false;
      label.textContent = inCart ? t('in_cart_view') : (r.valid ? r.label : r.reason);
      btn.toggleAttribute('data-in-cart', inCart);
    });
    const title = doc.querySelector(`[data-ccp-for="${this.id}"] [data-ccp-exp-sticky-title]`);
    if (title) { const opt = this.querySelector('input[name="ccp-exp-pkg"]:checked'); const tt = opt && opt.closest('label').querySelector('.ccp-opt__title'); if (tt) title.textContent = tt.firstChild ? tt.firstChild.textContent.trim() : tt.textContent; }
    this.syncGallery();
  }
  syncGallery() {
    const g = this.closest('.ccp-pdp') && this.closest('.ccp-pdp').querySelector('ccp-gallery');
    if (!g || !g.filter) return;
    const ids = this.picked();
    const p = this.pkg;
    if (p === 'volume') g.filter((s) => hasSet(s, 'vol') || hasSet(s, 'cover'), t('ctx_volume', { count: cfg().cases.length }));
    else if (!ids.length) g.filter((s) => hasSet(s, 'cover'), t('ctx_choose'));
    else if (p === 'trial') g.filter((s) => hasSet(s, 'case') && ids.includes(s.getAttribute('data-variant')), t('ctx_trial'));
    else g.filter((s) => hasSet(s, 'cover') && ids.includes(s.getAttribute('data-variant')), t('ctx_picked', { picked: ids.length, count: this.need }));
  }
  async submit(button) {
    const r = this.compute();
    if (button && button.hasAttribute('data-in-cart')) { const d = drawer(); if (d) { d.render(Cart.state); d.open(button); } return; }
    if (!r.valid) {
      if (this.picker) {
        this.picker.hidden = false;
        this.picker.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
        const f = this.picker.querySelector('input:not(:disabled):not(:checked)');
        if (f) setTimeout(() => f.focus({ preventScroll: true }), 250);
      }
      if (this.status) { this.status.textContent = r.reason; this.status.className = 'ccp-msg ccp-msg--error'; }
      return;
    }
    if (this.status) this.status.textContent = '';
    await addAndOpen(r.lines, button, this.status);
    this.refresh();
  }
}

/* ---------------- Sticky purchase bar ---------------- */
class CcpStickyBuy extends HTMLElement {
  connectedCallback() {
    const watchSel = this.getAttribute('data-watch');
    const hideSel = this.getAttribute('data-hide-when');
    this.watch = watchSel ? doc.querySelector(watchSel) : null;
    this.hideAt = hideSel ? [...doc.querySelectorAll(hideSel)] : [];
    this.mobileOnly = this.classList.contains('ccp-sticky--mobile-only');
    this.state = { past: false, blocked: false };
    const apply = () => {
      const show = this.state.past && !this.state.blocked;
      this.toggleAttribute('data-show', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.toggleAttribute('inert', !show);
      doc.body.classList.toggle('ccp-has-sticky', show);
      doc.body.classList.toggle('ccp-sticky-mobile-only', show && this.mobileOnly);
    };
    if (!this.watch || !('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === this.watch) this.state.past = !e.isIntersecting && e.boundingClientRect.top < 0;
        else { this._blocks = this._blocks || new Map(); this._blocks.set(e.target, e.isIntersecting); this.state.blocked = [...this._blocks.values()].some(Boolean); }
      });
      apply();
    });
    this.io.observe(this.watch);
    this.hideAt.forEach((el) => this.io.observe(el));
    this.onClick = (e) => {
      const btn = e.target.closest('[data-ccp-submit]');
      if (!btn) return;
      e.preventDefault();
      doc.dispatchEvent(new CustomEvent('ccp:submit', { detail: { formId: this.getAttribute('data-form'), button: btn } }));
    };
    this.addEventListener('click', this.onClick);
    apply();
  }
  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.onClick) this.removeEventListener('click', this.onClick);
    doc.body.classList.remove('ccp-has-sticky', 'ccp-sticky-mobile-only');
  }
}

/* ---------------- Promotion countdown (only for a real, fixed deadline) ---------------- */
class CcpCountdown extends HTMLElement {
  connectedCallback() {
    const end = Date.parse(this.getAttribute('data-end') || '');
    this.box = this.closest('[data-ccp-offer]') || this;
    if (!end || end <= Date.now()) { this.box.hidden = true; return; }
    this.end = end;
    this.parts = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
    this.tick = () => {
      const left = this.end - Date.now();
      if (left <= 0) { this.box.hidden = true; clearInterval(this.timer); return; }
      const d = Math.floor(left / 864e5), h = Math.floor((left % 864e5) / 36e5), m = Math.floor((left % 36e5) / 6e4), s = Math.floor((left % 6e4) / 1e3);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.parts.d) this.parts.d.textContent = pad(d);
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
    };
    this.box.hidden = false;
    this.tick();
    this.timer = setInterval(this.tick, 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
}

/* ---------------- Frequently bought together / add-selected ---------------- */
class CcpFbt extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-ccp-fbt-add]');
    this.totalEl = this.querySelector('[data-ccp-fbt-total]');
    this.wasEl = this.querySelector('[data-ccp-fbt-was]');
    this.saveEl = this.querySelector('[data-ccp-fbt-save]');
    this.countEl = this.querySelector('[data-ccp-fbt-count]');
    this.self = this.dataset.self || '';
    this.selfPrice = parseInt(this.dataset.selfPrice || '0', 10) || 0;
    this.onChange = () => this.update();
    this.onClick = (e) => { e.preventDefault(); const ids = this.toAdd(); if (ids.length) addAndOpen(ids.map((id) => ({ id, quantity: 1 })), this.btn, this.querySelector('[data-ccp-status]')); };
    this.onCart = () => this.update();
    this.addEventListener('change', this.onChange);
    if (this.btn) this.btn.addEventListener('click', this.onClick);
    doc.addEventListener('ccp:cart', this.onCart);
    this.update();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.btn) this.btn.removeEventListener('click', this.onClick);
    doc.removeEventListener('ccp:cart', this.onCart);
  }
  inCart(id) { return !!(Cart.state && Cart.lineFor(id)); }
  selected() { return [...this.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)')].map((i) => i.value); }
  toAdd() { const ids = this.selected(); if (this.self && !this.inCart(this.self)) ids.unshift(this.self); return ids; }
  priceOf(id) { if (id === this.self) return this.selfPrice; const cs = caseByVariant(id); if (cs) return cs.price; return parseInt((this.querySelector(`input[value="${id}"]`) || {}).dataset?.price, 10) || 0; }
  cartCases() { return Cart.state ? Cart.state.items.filter((it) => caseByVariant(it.variant_id)).reduce((n, it) => n + it.quantity, 0) : 0; }
  update() {
    this.querySelectorAll('input[type="checkbox"]').forEach((i) => {
      const inCart = Cart.state && cfg().limitQty && Cart.lineFor(i.value);
      const note = i.closest('label').querySelector('[data-ccp-incart]');
      if (inCart) { i.checked = false; i.disabled = true; } else if (!i.hasAttribute('data-unavailable')) i.disabled = false;
      if (note) note.hidden = !inCart;
    });
    const ids = this.toAdd();
    const subtotal = ids.reduce((n, id) => n + this.priceOf(id), 0);
    const newCases = ids.filter((id) => id === this.self || caseByVariant(id)).length;
    const before = this.cartCases();
    const tA = bestTier(before + newCases); const tB = bestTier(before);
    const extra = Math.max(0, (tA ? tA.discount : 0) - (tB ? tB.discount : 0));
    const pay = Math.max(0, subtotal - extra);
    if (this.totalEl) this.totalEl.textContent = money(pay);
    if (this.wasEl) { this.wasEl.hidden = !(extra > 0); this.wasEl.textContent = extra > 0 ? money(subtotal) : ''; }
    if (this.saveEl) { this.saveEl.hidden = !(extra > 0); this.saveEl.textContent = extra > 0 ? t('save_amount_short', { amount: money(extra) }) : ''; }
    if (this.countEl) this.countEl.textContent = ids.length ? t('fbt_count', { count: ids.length }) : '';
    const allIn = !ids.length && [...this.querySelectorAll('input[type="checkbox"]')].every((i) => i.disabled) && (!this.self || this.inCart(this.self));
    const sum = this.querySelector('.ccp-fbt__sum'); if (sum) sum.hidden = !ids.length;
    if (this.btn) {
      this.btn.disabled = !ids.length;
      const l = this.btn.querySelector('[data-ccp-label]');
      if (l) l.textContent = ids.length ? t('add_selected', { count: ids.length, price: money(pay) }) : (allIn ? t('fbt_done') : t('select_items'));
    }
  }
}

/* ---------------- Build your volume ---------------- */
class CcpVolumeBuilder extends HTMLElement {
  connectedCallback() {
    this.inputs = () => [...this.querySelectorAll('input[data-ccp-case]')];
    this.addBtn = this.querySelector('[data-ccp-builder-add]');
    this.allBtn = this.querySelector('[data-ccp-builder-all]');
    this.status = this.querySelector('[data-ccp-status]');
    this.nudge = this.querySelector('[data-ccp-nudge]');
    this.onChange = (e) => { if (e && e.target && e.target.dataset) e.target.dataset.touched = '1'; this.update(); };
    this.onClick = (e) => {
      if (e.target.closest('[data-ccp-builder-all]')) {
        e.preventDefault();
        const all = this.inputs().filter((i) => !i.disabled);
        const allOn = all.every((i) => i.checked);
        all.forEach((i) => { i.checked = !allOn; });
        this.update();
      } else if (e.target.closest('[data-ccp-builder-add]')) {
        e.preventDefault();
        this.add();
      } else if (e.target.closest('[data-ccp-builder-volume]')) {
        e.preventDefault();
        this.inputs().filter((i) => !i.disabled).forEach((i) => { i.checked = true; });
        this.update();
        this.addBtn && this.addBtn.focus();
      }
    };
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.onCart = () => this.update();
    doc.addEventListener('ccp:cart', this.onCart);
    this.update();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    doc.removeEventListener('ccp:cart', this.onCart);
  }
  selection() { return this.inputs().filter((i) => i.checked && !i.disabled).map((i) => i.value); }
  allSelected() { const avail = this.inputs().filter((i) => !i.hasAttribute('data-unavailable')); return avail.length > 0 && avail.every((i) => i.checked); }
  compute() {
    const c = cfg();
    const ids = this.selection();
    const subtotal = ids.reduce((n, id) => { const cs = caseByVariant(id); return n + (cs ? cs.price : 0); }, 0);
    const useVolume = this.allSelected() && c.volume && c.volume.available && c.volume.price > 0 && this.hasAttribute('data-use-volume');
    if (useVolume) return { ids, subtotal, total: c.volume.price, discount: Math.max(0, subtotal - c.volume.price), volume: true };
    const tier = bestTier(ids.length);
    const discount = tier ? Math.min(tier.discount, subtotal) : 0;
    return { ids, subtotal, total: subtotal - discount, discount, tier };
  }
  update() {
    if (Cart.state && cfg().limitQty) this.inputs().forEach((i) => { if (i.defaultChecked && !i.dataset.touched && Cart.lineFor(i.value)) { i.checked = false; i.dataset.touched = '1'; } });
    const c = cfg();
    const r = this.compute();
    const n = r.ids.length;
    const q = (s) => this.querySelector(s);
    const totalEl = q('[data-ccp-total]'), wasEl = q('[data-ccp-was]'), perEl = q('[data-ccp-per]'), saveEl = q('[data-ccp-save]');
    if (totalEl) totalEl.textContent = money(r.total);
    if (wasEl) { wasEl.textContent = r.discount > 0 ? money(r.subtotal) : ''; wasEl.hidden = !(r.discount > 0); }
    if (perEl) perEl.textContent = n > 1 ? t('per_file_short', { price: money(Math.round(r.total / n)) }) : '';
    if (saveEl) saveEl.textContent = r.discount > 0 ? t('save_amount_short', { amount: money(r.discount) }) : '';
    const countEl = q('[data-ccp-count]');
    if (countEl) countEl.textContent = t('selected_of', { count: n, total: this.inputs().length });
    const nextEl = q('[data-ccp-next-tier]');
    const nt = nextTier(n);
    if (nextEl) {
      if (!c.tiersEnabled) nextEl.textContent = '';
      else if (nt) { const est = this.estimateTier(nt); nextEl.textContent = t('builder_next', { count: nt.count - n, tier_count: nt.count, tier: nt.label, price: est ? money(est) : '' }); }
      else nextEl.textContent = n ? t('best_tier_reached') : '';
    }
    const bar = q('[data-ccp-bar]');
    if (bar) { const max = this.inputs().length || 1; bar.style.setProperty('--p', Math.round((n / max) * 100) + '%'); bar.parentElement.setAttribute('aria-valuenow', n); }
    if (this.nudge) {
      const v = c.volume;
      this.nudge.innerHTML = '';
      if (v && v.available && !r.volume && n > 0 && !this.allSelected() && r.total >= v.price && this.hasAttribute('data-use-volume')) {
        this.nudge.innerHTML = esc(t('builder_nudge', { price: money(v.price), count: this.inputs().length })) + ` <button type="button" data-ccp-builder-volume>${esc(t('select_all_short'))}</button>`;
      }
    }
    if (this.allBtn) { const all = this.inputs().filter((i) => !i.disabled); this.allBtn.textContent = all.length && all.every((i) => i.checked) ? t('clear_selection') : t('select_all', { count: numWord(all.length) }); }
    if (this.addBtn) {
      const label = this.addBtn.querySelector('[data-ccp-label]') || this.addBtn;
      this.addBtn.disabled = n === 0;
      label.textContent = n === 0 ? t('builder_empty') : (r.volume ? t('add_volume', { count: n, price: money(r.total) }) : t('add_n_files', { count: n, price: money(r.total) }));
    }
    // Mark items already in the cart.
    this.inputs().forEach((i) => {
      const note = i.closest('label').querySelector('[data-ccp-incart]');
      const inCart = Cart.state && Cart.lineFor(i.value);
      if (note) note.hidden = !inCart;
    });
  }
  estimateTier(tier) {
    const prices = cfg().cases.filter((x) => x.available).map((x) => x.price).sort((a, b) => a - b);
    const sel = this.selection().map((id) => (caseByVariant(id) || {}).price || 0);
    const need = tier.count - sel.length;
    if (need < 0 || prices.length < tier.count) return 0;
    const unselected = cfg().cases.filter((x) => x.available && !this.selection().includes(String(x.variantId))).map((x) => x.price).sort((a, b) => a - b);
    const sum = sel.reduce((a, b) => a + b, 0) + unselected.slice(0, need).reduce((a, b) => a + b, 0);
    const allSame = prices.every((p) => p === prices[0]);
    return allSame ? sum - tier.discount : 0;
  }
  add() {
    const r = this.compute();
    if (!r.ids.length) { if (this.status) { this.status.textContent = t('builder_empty'); this.status.className = 'ccp-msg ccp-msg--error'; } return; }
    const items = r.volume ? [{ id: cfg().volume.variantId, quantity: 1 }] : r.ids.map((id) => ({ id, quantity: 1 }));
    addAndOpen(items, this.addBtn, this.status).then((res) => {
      if (res && res.added && res.added.length) { this.inputs().forEach((i) => { i.checked = false; }); this.update(); }
    });
  }
}

/* ---------------- Investigation board ---------------- */
class CcpBoard extends HTMLElement {
  connectedCallback() {
    this.svg = this.querySelector('svg.ccp-board__svg');
    this.cards = [...this.querySelectorAll('.ccp-poi')];
    this.live = this.querySelector('[data-ccp-board-live]');
    if (!this.cards.length) return;
    const links = [];
    for (let i = 0; i < this.cards.length - 1; i++) links.push([i, i + 1]);
    for (let i = 0; i < this.cards.length - 2; i += 2) links.push([i, i + 2]);
    this.links = links;
    this.draw = () => {
      if (!this.svg) return;
      const box = this.getBoundingClientRect();
      this.svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      const pins = this.cards.map((c) => { const p = c.querySelector('.ccp-poi__pin').getBoundingClientRect(); return { x: p.left + p.width / 2 - box.left, y: p.top + p.height / 2 - box.top }; });
      this.svg.innerHTML = this.links.map(([a, b], k) => {
        const A = pins[a], B = pins[b];
        const sag = 40 + (k % 3) * 22;
        const d = `M ${A.x.toFixed(1)} ${A.y.toFixed(1)} Q ${((A.x + B.x) / 2).toFixed(1)} ${(Math.max(A.y, B.y) + sag).toFixed(1)} ${B.x.toFixed(1)} ${B.y.toFixed(1)}`;
        return `<path d="${d}" data-a="${a}" data-b="${b}" style="--len:${Math.round(Math.hypot(B.x - A.x, B.y - A.y) * 1.4)}"></path>`;
      }).join('') + pins.map((p) => `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4"></circle>`).join('');
      this.highlight(this.active);
    };
    this.onClick = (e) => {
      const card = e.target.closest('.ccp-poi');
      if (!card) return;
      const i = this.cards.indexOf(card);
      this.active = this.active === i ? null : i;
      this.cards.forEach((c, k) => c.setAttribute('aria-pressed', k === this.active ? 'true' : 'false'));
      this.highlight(this.active);
      if (this.live) {
        const name = card.querySelector('.ccp-poi__name');
        this.live.textContent = this.active == null ? t('board_cleared') : t('board_focus', { name: name ? name.textContent : '' });
      }
    };
    this.addEventListener('click', this.onClick);
    this.ro = new ResizeObserver(() => this.draw());
    this.ro.observe(this);
    if ('IntersectionObserver' in window && !reduceMotion()) {
      this.io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { this.setAttribute('data-drawn', ''); this.io.disconnect(); } }), { threshold: 0.3 });
      this.io.observe(this);
    } else this.setAttribute('data-drawn', '');
    if (designMode()) this.setAttribute('data-drawn', '');
    this.draw();
  }
  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
    this.removeEventListener('click', this.onClick);
  }
  highlight(i) {
    if (!this.svg) return;
    this.svg.querySelectorAll('path').forEach((p) => {
      const on = i != null && (p.dataset.a === String(i) || p.dataset.b === String(i));
      p.classList.toggle('is-hot', on);
      p.classList.toggle('is-dim', i != null && !on);
    });
  }
}

/* ---------------- Registration ---------------- */
const defs = {
  'ccp-marquee': CcpMarquee, 'ccp-header': CcpHeader, 'ccp-cart-drawer': CcpCartDrawer, 'ccp-add-button': CcpAddButton,
  'ccp-scroller': CcpScroller, 'ccp-gallery': CcpGallery, 'ccp-product-form': CcpProductForm, 'ccp-sticky-buy': CcpStickyBuy,
  'ccp-countdown': CcpCountdown, 'ccp-fbt': CcpFbt, 'ccp-volume-builder': CcpVolumeBuilder, 'ccp-board': CcpBoard, 'ccp-experience': CcpExperience
};
Object.keys(defs).forEach((name) => { if (!customElements.get(name)) customElements.define(name, defs[name]); });

initReveal();
doc.addEventListener('shopify:section:load', (e) => initReveal(e.target));

/* ---------------- Red thread between pinned cards ---------------- */
const SVGNS = 'http://www.w3.org/2000/svg';
function initThreads(scope) {
  (scope || doc).querySelectorAll('[data-ccp-thread]').forEach((track) => {
    if (track._ccpThread) return;
    track._ccpThread = true;
    const svg = doc.createElementNS(SVGNS, 'svg');
    svg.setAttribute('class', 'ccp-thread');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = doc.createElementNS(SVGNS, 'path');
    svg.appendChild(path);
    track.appendChild(svg);
    let drawn = reduceMotion() || designMode();
    const draw = () => {
      const pins = [...track.querySelectorAll('[data-ccp-pin]')].filter((p) => p.offsetParent);
      svg.setAttribute('width', track.scrollWidth);
      svg.setAttribute('height', track.scrollHeight);
      if (pins.length < 2) { path.setAttribute('d', ''); return; }
      // Layout positions (offsets) ignore the reveal transforms, so the thread lands where the cards settle.
      const pts = pins.map((p) => { const card = p.offsetParent; return [card.offsetLeft + p.offsetLeft + p.offsetWidth / 2, card.offsetTop + p.offsetTop + p.offsetHeight / 2]; });
      let d = `M${pts[0][0]} ${pts[0][1]}`;
      for (let i = 1; i < pts.length; i++) {
        const [x0, y0] = pts[i - 1], [x1, y1] = pts[i];
        const sag = Math.min(70, Math.abs(x1 - x0) * 0.2) + (i % 2 ? 12 : 0);
        d += ` Q ${(x0 + x1) / 2} ${Math.max(y0, y1) + sag} ${x1} ${y1}`;
      }
      path.setAttribute('d', d);
      const len = Math.ceil(path.getTotalLength());
      path.style.strokeDasharray = String(len);
      path.style.strokeDashoffset = drawn ? '0' : String(len);
    };
    draw();
    if ('ResizeObserver' in window) new ResizeObserver(draw).observe(track);
    track.querySelectorAll('img').forEach((im) => { if (!im.complete) im.addEventListener('load', draw, { once: true }); });
    if (!drawn && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setTimeout(() => { drawn = true; void path.getBoundingClientRect(); path.style.strokeDashoffset = '0'; }, 650);
      }, { threshold: 0.25 });
      io.observe(track);
    } else { drawn = true; draw(); }
  });
}
initThreads();
doc.addEventListener('shopify:section:load', (e) => initThreads(e.target));

/* ---------------- Silent looping videos ---------------- */
let loopObserver;
function initLoops(scope) {
  const vids = [...(scope || doc).querySelectorAll('video.ccp-loop, .ccp-vloop video, .ccp-stage__media video')];
  if (!vids.length) return;
  if (!loopObserver && 'IntersectionObserver' in window) {
    loopObserver = new IntersectionObserver((entries) => entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && !reduceMotion()) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
    }), { threshold: 0.15 });
  }
  vids.forEach((v) => {
    v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true;
    v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.removeAttribute('controls');
    v.setAttribute('disablepictureinpicture', ''); v.setAttribute('aria-hidden', 'true'); v.tabIndex = -1;
    if (reduceMotion()) { v.removeAttribute('autoplay'); v.pause(); return; }
    if (loopObserver) loopObserver.observe(v); else { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
  });
}
initLoops();
doc.addEventListener('shopify:section:load', (e) => initLoops(e.target));
// Theme Editor: bring selected blocks into view and open collapsed containers.
doc.addEventListener('shopify:block:select', (e) => {
  const el = e.target;
  const details = el.closest('details') || (el.tagName === 'DETAILS' ? el : null);
  if (details) details.open = true;
  const track = el.closest('[data-ccp-track]');
  if (track) track.scrollTo({ left: el.offsetLeft - track.offsetLeft, behavior: 'auto' });
  el.querySelectorAll && el.querySelectorAll('[data-ccp-reveal]').forEach((n) => n.setAttribute('data-in', ''));
});

/* ---------------- Mobile card rows: swipe + gentle autoplay ----------------
   [data-ccp-carousel] grids become a swipeable row on phones (CSS). Those rows and [data-ccp-auto] tracks
   advance one card every few seconds while at least half visible; any touch, scroll, hover or focus pauses them.
   Off for reduced motion and in the Theme Editor. */
const ccpMobile = window.matchMedia('(max-width: 749px)');
const AUTO_MS = 4600;
function initCarousels(scope) {
  const tracks = [...(scope || doc).querySelectorAll('[data-ccp-carousel], [data-ccp-track][data-ccp-auto]')];
  tracks.forEach((track) => {
    if (track._ccpCar) return;
    track._ccpCar = true;
    const items = () => [...track.children].filter((el) => !(el instanceof SVGElement) && el.getClientRects().length && getComputedStyle(el).position !== 'absolute').sort((x, y) => x.offsetLeft - y.offsetLeft);
    const scrollable = () => track.scrollWidth > track.clientWidth + 4;
    // Layout offsets ignore reveal transforms. offsetLeft is relative to the offsetParent, so compare with the first card.
    const posOf = (el) => { const first = items()[0]; return el.offsetLeft - (first ? first.offsetLeft : 0); };
    const current = () => {
      const list = items(); let best = 0, d = Infinity;
      list.forEach((el, i) => { const dd = Math.abs(posOf(el) - track.scrollLeft); if (dd < d) { d = dd; best = i; } });
      return best;
    };
    const goTo = (i, smooth = true) => {
      const list = items(); if (!list[i]) return;
      track.scrollTo({ left: Math.max(0, posOf(list[i])), behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
    };
    // Dots (phones only, decorative: swiping is the primary control).
    let dots = null;
    if (track.hasAttribute('data-ccp-carousel')) {
      dots = doc.createElement('div');
      dots.className = 'ccp-dots';
      dots.setAttribute('aria-hidden', 'true');
      dots.style.setProperty('--dur', AUTO_MS + 'ms');
      dots.innerHTML = items().map(() => '<span></span>').join('');
      track.after(dots);
      dots.addEventListener('click', (e) => { const sp = e.target.closest('span'); if (sp) { pause(9000); goTo([...dots.children].indexOf(sp)); } });
    }
    const paint = () => {
      if (!dots) return;
      const list = items(); const max = track.scrollWidth - track.clientWidth - 4;
      const i = track.scrollLeft >= max ? list.length - 1 : current();
      [...dots.children].forEach((d, k) => d.classList.toggle('is-on', k === i));
    };
    let raf = 0;
    track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; paint(); }); }, { passive: true });
    // Start position (e.g. the highlighted offer).
    const start = track.getAttribute('data-ccp-start');
    let started = !start;
    const applyStart = () => {
      if (started || !ccpMobile.matches || !scrollable()) return;
      started = true;
      const el = track.querySelector(':scope > ' + start);
      if (el && track.scrollLeft < 5) { const i = items().indexOf(el); if (i > -1) { track.scrollLeft = Math.max(0, posOf(el)); paint(); } }
    };
    if (doc.readyState === 'complete') setTimeout(applyStart, 60); else window.addEventListener('load', () => setTimeout(applyStart, 60), { once: true });
    ['pointerdown', 'touchstart'].forEach((ev) => track.addEventListener(ev, () => { started = true; }, { passive: true, once: true }));
    paint();
    if (reduceMotion() || designMode()) return;
    let visible = false, until = 0, timer = 0;
    const pause = (ms) => { until = Date.now() + ms; if (dots) dots.classList.add('is-paused'); };
    ['pointerdown', 'touchstart', 'wheel', 'focusin', 'keydown'].forEach((ev) => track.addEventListener(ev, () => pause(10000), { passive: true }));
    track.addEventListener('mouseenter', () => pause(60000));
    track.addEventListener('mouseleave', () => pause(1500));
    const tick = () => {
      if (track._ccpPinned || !ccpMobile.matches || !visible || doc.hidden || Date.now() < until || !scrollable()) return;
      if (dots) { dots.classList.remove('is-paused'); }
      const list = items(); const max = track.scrollWidth - track.clientWidth - 4;
      let i = current() + 1;
      if (track.scrollLeft >= max || i >= list.length) i = 0;
      goTo(i);
      if (dots) { const on = dots.querySelector('.is-on'); if (on) { on.classList.remove('is-on'); void on.offsetWidth; } }
    };
    const io = new IntersectionObserver((es) => {
      visible = es[0].isIntersecting && es[0].intersectionRatio >= 0.45;
      clearInterval(timer);
      if (visible) timer = setInterval(tick, AUTO_MS);
    }, { threshold: [0, 0.45, 0.7] });
    io.observe(track);
  });
}
initCarousels();
doc.addEventListener('shopify:section:load', (e) => { initCarousels(e.target); initPins(e.target); });

/* ---------------- Phones: card rows move sideways as you scroll down ----------------
   Each row (with its dots) is wrapped in a tall block whose inner part sticks under the header; the vertical
   distance scrolled through the block drives the row's horizontal position. Rows too tall for the screen, the
   Theme Editor and reduced motion keep the normal swipe. */
const pins = [];
let pinRaf = 0;
function pinTop() {
  const h = doc.querySelector('.ccp-header, #header-group');
  const b = h ? h.getBoundingClientRect().bottom : 0;
  return Math.min(120, Math.max(0, b));
}
function updatePins() {
  pinRaf = 0;
  pins.forEach((p) => {
    if (!p.on) return;
    const r = p.wrap.getBoundingClientRect();
    const prog = Math.min(1, Math.max(0, (p.top - r.top) / p.dist));
    // Rest on each card, then glide to the next one (smoothstep between plateaus).
    const n = p.pos.length;
    const f = prog * (n - 1);
    const i = Math.min(n - 2, Math.floor(f));
    const local = f - i;
    const k = Math.min(1, Math.max(0, (local - 0.22) / 0.56));
    const e = k * k * (3 - 2 * k);
    const x = p.pos[i] + (p.pos[i + 1] - p.pos[i]) * e;
    p.track.scrollLeft = Math.round(Math.min(p.max, x));
    // The card that has just settled gets a short zoom on its photo.
    const pinned = r.top <= p.top + 40 && r.bottom >= p.top + p.stick.offsetHeight - 40;
    if (!pinned) { p.cur = -1; return; }
    const cur = e > 0.9 ? i + 1 : (e < 0.1 ? i : -1);
    if (cur > -1 && cur !== p.cur && p.kids && p.kids[cur]) {
      p.cur = cur;
      const card = p.kids[cur];
      p.kids.forEach((k) => k.classList.remove('is-focus')); void card.offsetWidth; card.classList.add('is-focus');
    }
  });
}
function layoutPins() {
  const mobile = ccpMobile.matches;
  const vh = window.innerHeight;
  const top = pinTop();
  const bottomReserve = 84; // sticky buy bar
  pins.forEach((p) => {
    const off = () => { p.on = false; p.track._ccpPinned = false; p.wrap.classList.remove('is-on'); p.wrap.style.height = ''; p.stick.style.top = ''; p.track.style.scrollSnapType = ''; };
    if (!mobile) { off(); return; }
    p.track.style.scrollSnapType = 'none';
    const max = p.track.scrollWidth - p.track.clientWidth;
    const h = p.stick.offsetHeight;
    const avail = vh - top - bottomReserve;
    if (max < 24 || h > avail) { off(); return; }
    p.max = max;
    const kids = [...p.track.children].filter((el) => !(el instanceof SVGElement) && el.getClientRects().length && getComputedStyle(el).position !== 'absolute').sort((x, y) => x.offsetLeft - y.offsetLeft);
    if (kids.length < 2) { off(); return; }
    p.pos = kids.map((el) => Math.min(max, el.offsetLeft - kids[0].offsetLeft));
    p.kids = kids;
    p.dist = Math.round((kids.length - 1) * vh * 0.6);
    p.top = Math.round(top + (avail - h) / 2);
    p.on = true;
    p.track._ccpPinned = true;
    p.wrap.classList.add('is-on');
    p.wrap.style.height = (h + p.dist) + 'px';
    p.stick.style.top = p.top + 'px';
  });
  updatePins();
}
function initPins(scope) {
  if (reduceMotion() || designMode()) return;
  const sc = scroller();
  (scope || doc).querySelectorAll('[data-ccp-hscroll]').forEach((track) => {
    if (track._ccpPin) return;
    track._ccpPin = true;
    const wrap = doc.createElement('div'); wrap.className = 'ccp-hpin';
    const stick = doc.createElement('div'); stick.className = 'ccp-hpin__stick';
    const dots = track.nextElementSibling && track.nextElementSibling.classList.contains('ccp-dots') ? track.nextElementSibling : null;
    track.before(wrap); wrap.appendChild(stick); stick.appendChild(track);
    if (dots) stick.appendChild(dots);
    track.style.scrollBehavior = 'auto';
    // position: sticky needs every ancestor up to the scroller to stop being a scroll box: clip instead of hidden.
    for (let el = wrap.parentElement; el && el !== sc && el !== doc.body; el = el.parentElement) {
      const cs = getComputedStyle(el);
      if (/(hidden|auto|scroll)/.test(cs.overflowX + cs.overflowY)) el.style.overflow = 'clip';
    }
    const p = { track, wrap, stick, on: false };
    pins.push(p);
    if ('ResizeObserver' in window) new ResizeObserver(() => requestAnimationFrame(layoutPins)).observe(stick);
  });
  layoutPins();
}
initPins();
(function bindPins() {
  const on = () => { if (!pinRaf) pinRaf = requestAnimationFrame(updatePins); };
  window.addEventListener('scroll', on, { passive: true });
  const pw = doc.querySelector('.page-wrapper');
  if (pw) pw.addEventListener('scroll', on, { passive: true });
  window.addEventListener('resize', () => requestAnimationFrame(layoutPins));
  if (ccpMobile.addEventListener) ccpMobile.addEventListener('change', layoutPins);
  window.addEventListener('load', layoutPins);
})();

/* ---------------- Section buttons that jump to the buy box ---------------- */
doc.addEventListener('click', (e) => {
  const a = e.target.closest('a[data-ccp-jump]');
  if (!a) return;
  const target = doc.getElementById('ccp-buy') || doc.querySelector('[data-ccp-buy-anchor]');
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
  target.classList.remove('ccp-flash'); void target.offsetWidth; target.classList.add('ccp-flash');
  const btn = target.querySelector('button[type="submit"], [data-ccp-submit], .ccp-btn');
  if (btn) setTimeout(() => btn.focus({ preventScroll: true }), reduceMotion() ? 0 : 650);
});

/* ---------------- Reading progress line ---------------- */
(function initProgress() {
  if (designMode() || !doc.querySelector('.ccp-section, .ccp-s')) return;
  const bar = doc.createElement('div');
  bar.className = 'ccp-progress';
  bar.setAttribute('aria-hidden', 'true');
  doc.body.appendChild(bar);
  let raf = 0;
  const upd = () => {
    raf = 0;
    const el = scroller() || doc.documentElement;
    const max = (el.scrollHeight - el.clientHeight) || 1;
    bar.style.transform = `scaleX(${Math.min(1, Math.max(0, scrollTop() / max))})`;
  };
  const on = () => { if (!raf) raf = requestAnimationFrame(upd); };
  window.addEventListener('scroll', on, { passive: true });
  const pw = doc.querySelector('.page-wrapper');
  if (pw) pw.addEventListener('scroll', on, { passive: true });
  upd();
})();

/* ---------------- Count-up numbers & parallax ---------------- */
(function initMotionExtras() {
  if (reduceMotion() || designMode() || !root.classList.contains('ccp-motion') || !('IntersectionObserver' in window)) return;
  const nums = [...doc.querySelectorAll('.ccp-facts4 strong, .ccp-facts dd, .ccp-stats strong, .ccp-inv__count strong')].filter((el) => /^\s*\d{1,4}\s*$/.test(el.textContent));
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (!e.isIntersecting) return;
    io.unobserve(e.target);
    const el = e.target, end = parseInt(el.textContent, 10), t0 = performance.now(), dur = 1100;
    el.style.minWidth = el.offsetWidth + 'px';
    const step = (now) => { const k = Math.min(1, (now - t0) / dur); el.textContent = String(Math.round(end * (1 - Math.pow(1 - k, 3)))); if (k < 1) requestAnimationFrame(step); };
    el.textContent = '0'; requestAnimationFrame(step);
  }), { threshold: 0.6 });
  nums.forEach((el) => io.observe(el));
  const layers = [...doc.querySelectorAll('.ccp-cine__bg img, .ccp-hero .ccp-frame img')];
  layers.forEach((l) => l.setAttribute('data-ccp-parallax', ''));
  if (!layers.length) return;
  let raf = 0;
  const run = () => {
    raf = 0;
    const vh = window.innerHeight;
    layers.forEach((l) => {
      const r = l.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const p = (r.top + r.height / 2 - vh / 2) / vh; // -1..1 around the centre
      l.style.transform = `translate3d(0, ${(p * -28).toFixed(1)}px, 0) scale(1.08)`;
    });
  };
  const on = () => { if (!raf) raf = requestAnimationFrame(run); };
  window.addEventListener('scroll', on, { passive: true });
  const pw = doc.querySelector('.page-wrapper');
  if (pw) pw.addEventListener('scroll', on, { passive: true });
  run();
})();
